/**
 * A folder's own actions on the Assets page: Rename, Move to and Delete, behind one button on its card and beside its
 * name when it is open. The pattern is the prototype's menu (`openMenu`, `asMoveTo`): a menu of the actions, a list of
 * the places it can go, and a confirm for Delete that says what happens to what is inside.
 */
import { useState } from "react";
import type { LibraryAsset, LibraryFolder } from "../../shared/types";
import { Button } from "../components/ui/Button";
import { Field } from "../components/ui/Field";
import { Icon } from "../components/ui/Icon";
import { Menu } from "../components/ui/Menu";
import { Sheet } from "../components/ui/Sheet";
import { useToast } from "../components/ui/Toast";
import { folderName, folderPath, holds, moveTargets, usedWords } from "./library";
import { changeFolder, deleteFolder } from "./store";

type Action = "rename" | "move" | "delete";

const MAX_NAME = 40;

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * What Delete says: what the folder holds and that its files stay in Trash 30 days, then, apart in the callout, who
 * uses a file in it and keeps it ("X and Y use a file in it. They keep it.").
 */
export function deleteParts(folders: LibraryFolder[], assets: LibraryAsset[], id: string): { holds: string[]; keeps: string } {
  const { files, folders: inner } = holds(folders, assets, id);
  if (!files && !inner) return { holds: ["It is empty."], keeps: "" };
  const bits = [inner ? plural(inner, "folder", "folders") : "", files ? plural(files, "file", "files") : ""].filter(Boolean).join(" and ");
  const inside = assets.filter((a) => a.folder_id === id || a.folder_id?.startsWith(`${id}/`));
  return { holds: [`It has ${bits}.`, ...(files ? ["They stay in Trash for 30 days."] : [])], keeps: usedWords(inside.flatMap((a) => a.uses), "a file in it") };
}

export function FolderMenu({
  folder,
  folders,
  assets,
  onChanged,
  buttonClassName = "as-fmenu",
}: {
  folder: LibraryFolder;
  folders: LibraryFolder[];
  assets: LibraryAsset[];
  /** The folder's new id after a rename or a move, or null once it is deleted. */
  onChanged: (to: string | null) => void;
  /** The "..." button's look: the computer's card button, or the Season page's icon button. */
  buttonClassName?: string;
}) {
  const toast = useToast();
  const [action, setAction] = useState<Action | null>(null);
  const [name, setName] = useState(folder.name);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const targets = moveTargets(folders, folder.id);
  const gone = deleteParts(folders, assets, folder.id);

  const close = () => {
    if (busy) return;
    setAction(null);
    setError(null);
  };
  const run = async (work: () => Promise<string | null>, done: (to: string | null) => string) => {
    setBusy(true);
    setError(null);
    try {
      const to = await work();
      setBusy(false);
      setAction(null);
      onChanged(to);
      toast(done(to));
    } catch (e) {
      setBusy(false);
      setError(e instanceof Error ? e.message : "That didn’t work. Try again.");
    }
  };

  const rename = () => {
    const next = name.trim();
    if (!next) {
      setError("Give the folder a name first.");
      return;
    }
    if (next === folder.name) {
      close();
      return;
    }
    void run(() => changeFolder(folder.id, { name: next }), () => `Renamed to ${next}.`);
  };

  return (
    <>
      <Menu
        label={`Actions for ${folder.name}`}
        buttonClassName={buttonClassName}
        menuClassName="as-fm"
        items={[
          {
            label: "Rename",
            sub: "Give it a new name",
            icon: "pencil",
            onSelect: () => {
              setName(folder.name);
              setAction("rename");
            },
          },
          { label: "Move to", sub: "Put it in another folder", icon: "fmove", onSelect: () => setAction("move") },
          "sep",
          { label: "Delete", sub: "With everything in it", icon: "trash", danger: true, onSelect: () => setAction("delete") },
        ]}
      />
      <Sheet
        open={action === "rename"}
        onClose={close}
        title={`Rename ${folder.name}`}
        footer={
          <>
            <Button variant="q" onClick={close}>
              Cancel
            </Button>
            <Button variant="p" disabled={busy} onClick={rename}>
              Rename
            </Button>
          </>
        }
      >
        <Field label="Folder name" htmlFor="as-rename" error={error ?? undefined}>
          <input dir="auto"
            id="as-rename"
            className="inp"
            autoComplete="off"
            maxLength={MAX_NAME}
            autoFocus
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                rename();
              }
            }}
          />
        </Field>
      </Sheet>
      <Sheet open={action === "move"} onClose={close} title={`Move ${folder.name} to`} footer={<Button variant="q" onClick={close}>Cancel</Button>}>
        <div className="as-mvlist" role="group" aria-label="Places">
          {folder.parent_id ? (
            <button type="button" className="as-rr" disabled={busy} onClick={() => void run(() => changeFolder(folder.id, { parent_id: null }), () => "Moved to Assets.")}>
              <Icon name="allf" />
              <span className="sp">Assets, in no folder</span>
            </button>
          ) : null}
          {targets.map((t) => (
            <button
              type="button"
              className="as-rr"
              key={t.id}
              disabled={busy}
              onClick={() => void run(() => changeFolder(folder.id, { parent_id: t.id }), () => `Moved to ${folderName(folders, t.id)}.`)}
            >
              <Icon name="fold" />
              <span className="sp">{folderPath(folders, t.id).map((x) => x.name).join(" › ")}</span>
            </button>
          ))}
          {!folder.parent_id && !targets.length ? <p className="as-none">There is no other folder to move it into.</p> : null}
        </div>
        {error ? (
          <p className="as-mverr" role="alert">
            {error}
          </p>
        ) : null}
      </Sheet>
      <Sheet
        open={action === "delete"}
        onClose={close}
        title={`Delete ${folder.name}?`}
        footer={
          <>
            <Button variant="q" onClick={close}>
              Cancel
            </Button>
            <Button
              variant="p"
              className="as-bad"
              disabled={busy}
              onClick={() =>
                void run(
                  async () => (await deleteFolder(folder.id), null),
                  () => (holds(folders, assets, folder.id).files ? `Moved ${folder.name} to Trash.` : `Deleted ${folder.name}.`),
                )
              }
            >
              Delete folder
            </Button>
          </>
        }
      >
        <span className="as-dlg-ic" aria-hidden="true">
          <Icon name="trash" />
        </span>
        {gone.holds.map((line) => (
          <p className="as-delw" key={line}>
            {line}
          </p>
        ))}
        {gone.keeps ? <p className="as-dlg-note">{gone.keeps}</p> : null}
        {error ? (
          <p className="as-mverr" role="alert">
            {error}
          </p>
        ) : null}
      </Sheet>
    </>
  );
}
